import { readCookie, writeCookie } from '@zapier/cookies';
import { SplitFactory } from '@splitsoftware/splitio-react';
import { ISplitFactoryProps } from '@splitsoftware/splitio-react/types/types';
import { Fragment, useEffect, useRef, useState } from 'react';
import { isBrowser } from '../utils';

export const SplitioProvider = ({ children }: ISplitFactoryProps) => {
  const zapidentity = readCookie('zapidentity');
  const zapSplitKey = readCookie('__zsk');
  const [key, setkey] = useState(zapSplitKey || zapidentity);

  const intervalIndex = useRef(0);

  const pollForZapidentity = () => {
    const intervalID = setInterval(() => {
      intervalIndex.current += 1;
      const cookie = readCookie('zapidentity');
      if (cookie) {
        setkey(cookie);
        clearInterval(intervalID);
      } else if (intervalIndex.current >= 24) {
        /**
         * If zapidentity isn't available for some reason, we create a uuid and use that as the split key.
         * If the user has not consented to analytics cookies, then we set the split key to be 'anonymous_group'.
         */
        let uuid: string;
        if (window.crypto?.randomUUID) {
          uuid = window.crypto.randomUUID();
        } else {
          // Slice off "0." from the Math.randomw() string
          uuid = String(Math.random()).slice(2);
        }

        const success = writeCookie('__zsk', uuid, {
          category: 'analytics',
          maxAge: 365,
        });
        setkey(success ? uuid : 'anonymous_group');
        clearInterval(intervalID);
      }
    }, 2);
    return intervalID;
  };

  useEffect(() => {
    if (!isBrowser()) return;
    let intervalID: ReturnType<typeof setInterval>;
    /**
     * zapidentity is not available initially for first time visitors.
     * Because we can't listen for cookie changes, we need to poll for the cookie.
     * This is similar to how we are waiting for the cookie in TrackingRouting.tsx.
     */
    if (!key) {
      intervalID = pollForZapidentity();
    }
    return () => {
      if (intervalID) {
        clearInterval(intervalID);
      }
    };
  }, [key]);

  if (!key) return <Fragment>{children}</Fragment>;

  return (
    <SplitFactory
      config={{
        core: {
          authorizationKey: process.env.NEXT_PUBLIC_SPLITIO_AUTH_KEY || '',
          key,
        },
      }}
      updateOnSdkUpdate={true}
      updateOnSdkTimedout={true}
    >
      {children}
    </SplitFactory>
  );
};
